import { useEffect, useMemo, useState } from "react";
import { useConvex, usePaginatedQuery, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type { TranscriptEntry, TranscriptSummary } from "../../shared/types.ts";
import type { RoleAuth } from "./auth.ts";
import { beginTranscriptBootstrapRefresh } from "./transcriptBootstrapState.ts";

type TranscriptBootstrapResult = {
  entries: TranscriptEntry[];
  totalEntries: number;
  oldestTs: number | null;
  newestTs: number | null;
  projectionVersion: number;
};

type BootstrapState = TranscriptBootstrapResult & {
  sessionId: string | null;
  loading: boolean;
  loaded: boolean;
  error: string | null;
};

type PaginatedTranscript = {
  results: TranscriptEntry[];
  status: "LoadingFirstPage" | "CanLoadMore" | "LoadingMore" | "Exhausted";
  loadMore: (numItems: number) => void;
};

const FALLBACK_TRANSCRIPT_PAGE_SIZE = 5000;

const EMPTY_BOOTSTRAP: BootstrapState = {
  sessionId: null,
  entries: [],
  totalEntries: 0,
  oldestTs: null,
  newestTs: null,
  projectionVersion: 0,
  loading: false,
  loaded: false,
  error: null,
};

/** Shape of the feed returned by `useSessionTranscript`, for components that receive
 *  the dashboard-owned feed instead of subscribing again. */
export type SessionTranscriptFeed = ReturnType<typeof useSessionTranscript>;

export function useSessionTranscript({
  auth,
  sessionId,
  enabled,
  transcriptProjectionVersion = 0,
}: {
  auth: RoleAuth;
  sessionId: string | null;
  enabled: boolean;
  transcriptProjectionVersion?: number;
}) {
  const convex = useConvex();
  const [bootstrap, setBootstrap] = useState<BootstrapState>(EMPTY_BOOTSTRAP);
  const sessionIdArg = enabled && sessionId ? (sessionId as Id<"sessions">) : null;

  useEffect(() => {
    if (!sessionIdArg) {
      setBootstrap(EMPTY_BOOTSTRAP);
      return;
    }

    let cancelled = false;
    const requestedSessionId = sessionIdArg;
    const requestedVersion = transcriptProjectionVersion;
    setBootstrap((current) =>
      beginTranscriptBootstrapRefresh(current, requestedSessionId, requestedVersion)
    );

    async function loadBootstrap() {
      try {
        const result = await convex.query(convexApi.transcript.getTranscriptBootstrap, {
          auth,
          sessionId: requestedSessionId,
        }) as TranscriptBootstrapResult;
        if (cancelled) return;
        setBootstrap({
          sessionId: requestedSessionId,
          entries: result.entries,
          totalEntries: result.totalEntries,
          oldestTs: result.oldestTs,
          newestTs: result.newestTs,
          projectionVersion: result.projectionVersion,
          loading: false,
          loaded: true,
          error: null,
        });
      } catch (error) {
        if (cancelled) return;
        setBootstrap((current) => current.sessionId === requestedSessionId && current.loaded
          ? { ...current, projectionVersion: requestedVersion, error: errorMessage(error) }
          : {
              ...EMPTY_BOOTSTRAP,
              sessionId: requestedSessionId,
              projectionVersion: requestedVersion,
              loaded: true,
              error: errorMessage(error),
            });
      }
    }

    void loadBootstrap();
    return () => {
      cancelled = true;
    };
  }, [auth, convex, sessionIdArg, transcriptProjectionVersion]);

  const tailEntries = useQuery(
    convexApi.transcript.getTranscriptAfterTs,
    sessionIdArg && bootstrap.sessionId === sessionIdArg && bootstrap.loaded && !bootstrap.error
      ? { auth, sessionId: sessionIdArg, afterTs: bootstrap.newestTs ?? 0 }
      : "skip",
  ) as TranscriptEntry[] | undefined;
  const fallbackActive = Boolean(sessionIdArg && bootstrap.error && !bootstrap.loaded);
  const fallbackTranscript = usePaginatedQuery(
    convexApi.transcript.getTranscriptPage,
    fallbackActive && sessionIdArg
      ? { auth, sessionId: sessionIdArg }
      : "skip",
    { initialNumItems: FALLBACK_TRANSCRIPT_PAGE_SIZE },
  ) as PaginatedTranscript;

  const fallbackEntries = useMemo(
    () => [...(fallbackTranscript.results ?? [])].reverse(),
    [fallbackTranscript.results],
  );

  const entries = useMemo(
    () => bootstrap.error && !bootstrap.loaded
      ? fallbackEntries
      : mergeTranscriptEntries(bootstrap.entries, tailEntries ?? []),
    [bootstrap.entries, bootstrap.error, fallbackEntries, tailEntries],
  );

  const summary = useMemo<TranscriptSummary>(
    () => transcriptSummary(bootstrap, entries),
    [bootstrap, entries],
  );

  return {
    sessionId: bootstrap.sessionId,
    entries,
    summary,
    loading: bootstrap.loading || (fallbackActive && fallbackTranscript.status === "LoadingFirstPage"),
    error: bootstrap.error && fallbackActive && fallbackTranscript.status === "LoadingFirstPage" ? bootstrap.error : null,
    projectionVersion: bootstrap.projectionVersion,
  };
}

function mergeTranscriptEntries(
  bootstrapEntries: TranscriptEntry[],
  tailEntries: TranscriptEntry[],
): TranscriptEntry[] {
  if (tailEntries.length === 0) return bootstrapEntries;
  const byId = new Map<string, TranscriptEntry>();
  for (const entry of bootstrapEntries) byId.set(entry.id, entry);
  for (const entry of tailEntries) byId.set(entry.id, entry);
  return Array.from(byId.values()).sort((a, b) => a.ts - b.ts || a.id.localeCompare(b.id));
}

function transcriptSummary(
  bootstrap: BootstrapState,
  entries: TranscriptEntry[],
): TranscriptSummary {
  const oldestEntry = entries[0];
  const newestEntry = entries.at(-1);
  return {
    totalEntries: Math.max(bootstrap.totalEntries, entries.length),
    oldestTs: oldestEntry?.ts ?? bootstrap.oldestTs,
    newestTs: newestEntry?.ts ?? bootstrap.newestTs,
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Could not load transcript history";
}
